import type { TrendDataset } from '@/entities/insight';
import { getPlatformName, type SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';
import { generateDateArray } from '@/shared/utils/date/generate-date-array';
import dayjs from 'dayjs';
import { useMemo } from 'react';

type Props = {
  trends: TrendDataset[];
  selectedMetric: 'total' | 'like' | 'comment' | 'shared';
  startDate: string;
  endDate: string;
  dateUnit: 'day' | 'week' | 'month';
  colorPalette: {
    [p in SOURCE_PLATFORM_TYPE]: string;
  };
};

export const useEngagementStackChart = (props: Props) => {
  const dates = generateDateArray(dayjs(props.startDate), dayjs(props.endDate), props.dateUnit);

  const chartData = useMemo(() => {
    return dates.map((date) => {
      const base: {
        date: string;
      } & {
        [p in SOURCE_PLATFORM_TYPE]?: number;
      } = {
        date: date.format('YYYY-MM-DD'),
      };

      for (const trend of props.trends) {
        const target = trend.data.find(
          (trendData) =>
            dayjs()
              .year(trendData.date.year)
              .month(trendData.date.month - 1)
              .date(trendData.date.day)
              .format('YYYY-MM-DD') === date.format('YYYY-MM-DD'),
        );

        if (target) {
          if (props.selectedMetric === 'total') {
            base[trend.platform] = target.like + target.comment + target.shared;
          } else {
            base[trend.platform] = target[props.selectedMetric];
          }
        }
      }

      return base;
    });
  }, [dates, props.selectedMetric, props.trends]);

  const chartConfig = useMemo(() => {
    return props.trends.reduce(
      (acc, trend) => {
        acc[trend.platform] = {
          label: getPlatformName(trend.platform),
          color: props.colorPalette[trend.platform],
        };
        return acc;
      },
      {} as Record<SOURCE_PLATFORM_TYPE, { label: string; color: string }>,
    );
  }, [props.trends, props.colorPalette]);

  return {
    chartData,
    chartConfig,
  };
};
